import { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import {
  Input,
  Button,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import {
  ELASTIC_DIMENSION_FIELDS,
  ELASTIC_METRIC_FIELDS,
  ELASTIC_FIELDS,
  ELASTIC_FIELD_BY_NAME,
  ELASTIC_METRICS,
  ELASTIC_TEMPLATES,
  MAX_ELASTIC_DIMENSIONS,
} from './elastic-fields';

type Config = Record<string, unknown>;
interface Props {
  config: Config;
  onChange: (patch: Config) => void;
  showErrors?: boolean;
}

interface FilterRow {
  field: string;
  value: string;
}

const arr = (c: Config, k: string) => (Array.isArray(c[k]) ? (c[k] as unknown[]) : []);
const half: React.CSSProperties = { maxWidth: 420 };

function filtersToRows(filters: Record<string, unknown> | undefined): FilterRow[] {
  if (!filters) return [];
  return Object.entries(filters).map(([field, v]) => ({
    field,
    value: Array.isArray(v) ? v.join(', ') : String(v),
  }));
}

function rowsToFilters(rows: FilterRow[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const r of rows) {
    if (!r.field || !r.value.trim()) continue;
    const def = ELASTIC_FIELD_BY_NAME[r.field];
    const parts = r.value.split(',').map((s) => s.trim()).filter(Boolean);
    const vals = def?.kind === 'numeric' ? parts.map(Number).filter((n) => Number.isFinite(n)) : parts;
    out[r.field] = vals.length === 1 ? vals[0] : vals;
  }
  return out;
}

/** Structured builder for an elastic (WAF-log) monitor — group-by + metric +
 *  equality filters over the allow-listed field registry, plus quick templates. */
export function ElasticConfigForm({ config: c, onChange, showErrors }: Props) {
  const [rows, setRows] = useState<FilterRow[]>(() => filtersToRows(c.filters as Record<string, unknown>));

  const dimensions = useMemo(() => arr(c, 'dimensions').map(String), [c]);
  const domains = useMemo(() => arr(c, 'domains').map(String), [c]);
  const metric = (c.metric as string) || 'count';
  const noDomains = showErrors && domains.length === 0;

  const setRowsAndSync = (next: FilterRow[]) => {
    setRows(next);
    onChange({ filters: rowsToFilters(next) });
  };

  const toggleDim = (name: string) => {
    const has = dimensions.includes(name);
    if (!has && dimensions.length >= MAX_ELASTIC_DIMENSIONS) return;
    onChange({ dimensions: has ? dimensions.filter((d) => d !== name) : [...dimensions, name] });
  };

  const applyTemplate = (patch: Config) => {
    onChange(patch);
    setRows(filtersToRows((patch.filters as Record<string, unknown>) ?? {}));
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Field label="Quick templates" description="Prefill a common aggregation, then tweak below.">
        {() => (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
            {ELASTIC_TEMPLATES.map((t) => (
              <Button key={t.id} type="button" size="sm" variant="secondary" title={t.description} onClick={() => applyTemplate(t.patch)}>
                {t.label}
              </Button>
            ))}
          </div>
        )}
      </Field>

      <Field label="Domains" required error={noDomains ? 'Add at least one domain to scope the query.' : undefined}
        description="WAAP/CDN domains to aggregate over (comma-separated).">
        {(p) => (
          <Input
            {...p}
            value={domains.join(', ')}
            onChange={(e) => {
              const ds = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
              // The backend still requires a non-empty `query` (legacy domain
              // fallback); derive it from the first domain so it's always valid.
              onChange({ domains: ds, query: ds.length ? `domain:${ds[0]}` : '' });
            }}
            placeholder="www.example.com"
            invalid={!!noDomains}
          />
        )}
      </Field>

      <Field label="Group by" description={`Up to ${MAX_ELASTIC_DIMENSIONS} dimensions. Maps to the waflog field shown in each chip.`}>
        {() => (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
            {ELASTIC_DIMENSION_FIELDS.map((f) => {
              const active = dimensions.includes(f.name);
              const disabled = !active && dimensions.length >= MAX_ELASTIC_DIMENSIONS;
              return (
                <button key={f.name} type="button" disabled={disabled} onClick={() => toggleDim(f.name)} title={f.es}
                  style={{
                    cursor: disabled ? 'not-allowed' : 'pointer', font: 'inherit', fontSize: 'var(--vn-font-size-sm)',
                    padding: 'var(--vn-space-1) var(--vn-space-3)', borderRadius: 'var(--vn-radius-full)',
                    border: `1px solid ${active ? 'var(--vn-primary-border)' : 'var(--vn-border-default)'}`,
                    background: active ? 'var(--vn-primary-subtle)' : 'var(--vn-bg-surface)',
                    color: disabled ? 'var(--vn-fg-muted)' : 'var(--vn-fg-default)', opacity: disabled ? 0.5 : 1,
                  }}>
                  {f.label}
                </button>
              );
            })}
          </div>
        )}
      </Field>

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: '1fr 1fr', maxWidth: 420 }}>
        <Field label="Metric">
          {() => (
            <Select value={metric} onValueChange={(v) => onChange({ metric: v, ...(v === 'count' ? { metric_field: '' } : {}) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ELASTIC_METRICS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </Field>
        {metric !== 'count' && (
          <Field label="Metric field" required error={showErrors && !c.metric_field ? 'Required for this metric.' : undefined}>
            {() => (
              <Select value={(c.metric_field as string) || ''} onValueChange={(v) => onChange({ metric_field: v })}>
                <SelectTrigger><SelectValue placeholder="Pick field" /></SelectTrigger>
                <SelectContent>{ELASTIC_METRIC_FIELDS.map((f) => <SelectItem key={f.name} value={f.name}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </Field>
        )}
      </div>

      <Field label="Filters" description="Equality filters (multiple values = OR, comma-separated).">
        {() => (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {rows.map((row, i) => (
              <div key={i} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                <Select value={row.field} onValueChange={(v) => setRowsAndSync(rows.map((r, j) => (j === i ? { ...r, field: v } : r)))}>
                  <SelectTrigger style={{ minWidth: 180 }}><SelectValue placeholder="Field" /></SelectTrigger>
                  <SelectContent>{ELASTIC_FIELDS.map((f) => <SelectItem key={f.name} value={f.name}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={row.value} onChange={(e) => setRowsAndSync(rows.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)))} placeholder="value, value2" style={{ flex: 1 }} />
                <IconButton aria-label="Remove filter" variant="ghost" size="sm" onClick={() => setRowsAndSync(rows.filter((_, j) => j !== i))}>
                  <X size={15} />
                </IconButton>
              </div>
            ))}
            <div>
              <Button type="button" size="sm" variant="ghost" startIcon={<Plus size={14} />} onClick={() => setRows([...rows, { field: '', value: '' }])}>
                Add filter
              </Button>
            </div>
          </div>
        )}
      </Field>

      <Field label="Evaluation window (seconds)" description="Lookback window per aggregation. Leave blank to match the check interval (recommended). Must be ≥ check interval, else rolling windows leave gaps and miss traffic.">
        {(p) => <Input {...p} type="number" value={c.threshold_interval != null ? String(c.threshold_interval) : ''} onChange={(e) => onChange({ threshold_interval: e.target.value ? Number(e.target.value) : undefined })} placeholder="= check interval" style={half} />}
      </Field>
    </div>
  );
}
